import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material'
import { AdventureEditor } from '../components/editor'
import { useAdventureForEdit, useUpdateAdventure } from '../hooks/useAdventures'
import { LoadingSpinner } from '@components/common/LoadingSpinner'
import { adventuresApi } from '@api/endpoints/adventures'

export default function AdventureEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: adventure, isLoading, error } = useAdventureForEdit(id!)
  const updateMutation = useUpdateAdventure()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleSave = async (data: any) => {
    if (!id) return

    await updateMutation.mutateAsync({
      id,
      data: { ...data, id },
    })
  }

  const handlePublish = async (_data: any) => {
    if (!id) return

    // Call the dedicated publish endpoint
    await adventuresApi.publishAdventure(id)

    // Navigate to detail page after publishing
    navigate(`/adventures/${id}`)
  }

  const handleCancel = () => {
    navigate(`/adventures/${id}`)
  }

  const handleDelete = () => {
    setDeleteError(null)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!id) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await adventuresApi.deleteAdventure(id)
      setDeleteDialogOpen(false)
      navigate('/my-adventures')
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data || 'Failed to delete adventure'
      setDeleteError(message)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setDeleteError(null)
  }

  if (isLoading) return <LoadingSpinner fullPage />

  if (error || !adventure) {
    return (
      <Alert severity="error">
        Failed to load adventure. Please try again later.
      </Alert>
    )
  }

  return (
    <>
      <AdventureEditor
        adventureId={id}
        existingAdventure={adventure as any}
        onSave={handleSave}
        onPublish={handlePublish}
        onDelete={handleDelete}
        onCancel={handleCancel}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Adventure</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{adventure?.title}"? This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
